import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@mri/environment';
import { AuthSignInDto, AuthSignUpDto } from '@mri/app/dto';
import { AuthCache, ConfigMeasurementUnit } from '@mri/types';
import { map, mergeMap, Observable, of, Subject, tap } from 'rxjs';
import { LoadingService } from '@mri/app/modules';
import { ConfigResourceService } from '../config-resource';

const AUTH_TOKEN_STORAGE_KEY = '_auth_token';

@Injectable({
  providedIn: 'root'
})
export class AuthResourceService {

  private _auth?: AuthCache;
  private _authObs$ = new Subject<AuthCache>();
  private _isWaitingAuth?: boolean;

  onChangeAuth$ = new Subject<AuthCache>();

  get auth$(): Observable<AuthCache | undefined> {
    if (this._auth) {
      //console.log('Auth: return cache');
      return of(this._auth);
    }

    if (this._isWaitingAuth) {
      //console.log('Auth: return observable cache');
      return this._authObs$.asObservable();
    }

    //console.log('Auth: brig of server');
    return this.get();
  }

  get authFirst$(): Observable<AuthCache | undefined> {
    if (this._auth) return of(this._auth);

    return this.auth$;
  }

  get token(): string {
    return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || '';
  }

  get auth(): AuthCache | undefined {
    return this._auth;
  }

  set auth(value: AuthCache | undefined) {
    this._auth = value;

    if (value) {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, value?.session?.token);
    } else {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }
  }

  get header() {
    return { 'auth_token': this.token };
  }

  constructor(
    private _httpSrv: HttpClient,
    private _loadingSrv: LoadingService,
  ) {
  }

  signIn(body: AuthSignInDto) {
    const { apiUrl } = environment;

    return this._httpSrv.post<AuthCache>(`${apiUrl}/auth/sign-in`, body)
      .pipe(
        tap(auth => this.auth = auth)
      );
  }

  signUp(body: AuthSignUpDto) {
    const { apiUrl } = environment;

    return this._httpSrv.post<AuthCache>(`${apiUrl}/auth/sign-up`, body)
      .pipe(
        tap(auth => this._auth = auth)
      );
  }

  signOut() {
    const { apiUrl } = environment;

    return this._httpSrv.post<AuthCache>(`${apiUrl}/auth/sign-out`, {})
      .pipe(
        tap(() => this.auth = undefined)
      );
  }

  selectProperty(id: string) {
    const { apiUrl } = environment;

    this._loadingSrv.visible = true;
    return this._httpSrv.post<AuthCache>(`${apiUrl}/auth/select/${id}`, {}, { headers: this.header })
      .pipe(
        tap(auth => this._auth = auth),
        tap(() => window.location.reload())
      );
  }

  get() {
    const { apiUrl } = environment;

    return of({})
      .pipe(
        tap(() => this._isWaitingAuth = true),

        mergeMap(() => this._httpSrv.get<AuthCache>(`${apiUrl}/auth/`, { headers: this.header })),
        tap(auth => this.auth = auth),
        tap(auth => this._authObs$.next(auth)),

        tap(() => this._setFavicon()),
        tap(() => this._isWaitingAuth = false)
      );
  }



  private _setFavicon() {
    const { favicon } = this._auth?.personalize || {};
    if (!favicon?.url) return;

    document.querySelector('#favIcon')?.setAttribute('href', favicon.url);
  }




}
